import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client/react';

import { setArcgisLayerData } from '@atom/actions/arcgisLayerActions';
import {
  ARCGIS_LAYER_CREATE,
  ARCGIS_LAYER_DELETE,
  ARCGIS_LAYER_UPDATE,
  GET_ARCGIS_LAYERS,
} from '@atom/graph/arcgis';
import { Snackbar } from '@atom/mui';
import type {
  ArcgisLayer,
  ArcgisLayerCreateInput,
  ArcgisLayerUpdateInput,
  ArcgisOverlayLayer,
} from '@atom/types/arcgis';
import type { RootState } from '@atom/types/store';
import {
  addToSet,
  combineSets,
  removeFromSet,
} from '@atom/utilities/setUtilities';

import { useMapLayers } from '../MapPortalDataProvider';

import { loadLayerData } from './arcgisService';

type ArcgisLayersContextValue = {
  arcgisDictionary: ArcgisLayer[];
  layers: ArcgisOverlayLayer[];
  loading: boolean;
  fetchingIds: ReadonlySet<string>;
  add: (input: ArcgisLayerCreateInput) => Promise<ArcgisLayer | null>;
  update: (input: ArcgisLayerUpdateInput) => Promise<ArcgisLayer | null>;
  remove: (id: string) => Promise<void>;
  refresh: (layer: ArcgisLayer) => Promise<void>;
};

const ArcgisLayersContext = createContext<ArcgisLayersContextValue>({
  arcgisDictionary: [],
  layers: [],
  loading: false,
  fetchingIds: new Set(),
  add: async () => null,
  update: async () => null,
  remove: async () => {},
  refresh: async () => {},
});

export const useArcgisLayers = (): ArcgisLayersContextValue =>
  useContext(ArcgisLayersContext);

type ArcgisLayersProviderProps = {
  children: ReactNode;
};

const ArcgisLayersProvider = ({ children }: ArcgisLayersProviderProps) => {
  const {
    layers: { geospatialLayers, arcgisLayerIds },
  } = useMapLayers();

  const featureData = useSelector((state: RootState) => state.arcgisLayerData);
  const dispatch = useDispatch();

  // Tracks the link we've already fetched for each layer id so a re-render
  // doesn't refetch.
  const loadedLinksRef = useRef<Record<string, string>>({});

  const [fetchingIds, setFetchingIds] = useState<Set<string>>(new Set());

  const clearFetching = useCallback((id: string) => {
    setFetchingIds(prev => (prev.has(id) ? removeFromSet(prev, id) : prev));
  }, []);

  const storeLayerData = useCallback(
    async (layer: ArcgisLayer) => {
      const layerData = await loadLayerData(layer.link);
      dispatch(
        setArcgisLayerData(layer.id, {
          ...layerData,
          lastSynced: Date.now(),
        }),
      );
    },
    [dispatch],
  );

  const { data, loading } = useQuery(GET_ARCGIS_LAYERS, {
    notifyOnNetworkStatusChange: true,
  });

  const [createLayer] = useMutation(ARCGIS_LAYER_CREATE, {
    refetchQueries: [{ query: GET_ARCGIS_LAYERS }],
    awaitRefetchQueries: true,
  });

  const [updateLayer] = useMutation(ARCGIS_LAYER_UPDATE, {
    refetchQueries: [{ query: GET_ARCGIS_LAYERS }],
    awaitRefetchQueries: true,
  });

  const [deleteLayer] = useMutation(ARCGIS_LAYER_DELETE, {
    refetchQueries: [{ query: GET_ARCGIS_LAYERS }],
    awaitRefetchQueries: true,
  });

  const arcgisDictionary = useMemo(() => data?.arcgisLayers ?? [], [data]);

  const selectedLayers = useMemo(
    () =>
      geospatialLayers && arcgisLayerIds.size > 0
        ? [...arcgisLayerIds]
            .map(id => arcgisDictionary.find(layer => layer.id === id))
            .filter((layer): layer is ArcgisLayer => Boolean(layer))
        : [],
    [geospatialLayers, arcgisLayerIds, arcgisDictionary],
  );

  useEffect(() => {
    const pending = selectedLayers.filter(
      layer =>
        loadedLinksRef.current[layer.id] !== layer.link &&
        !featureData[layer.id],
    );

    const loadLayer = async (layer: ArcgisLayer) => {
      loadedLinksRef.current[layer.id] = layer.link;

      try {
        await storeLayerData(layer);
      } catch {
        if (loadedLinksRef.current[layer.id] === layer.link) {
          delete loadedLinksRef.current[layer.id];
        }
      } finally {
        clearFetching(layer.id);
      }
    };

    if (pending.length > 0) {
      const pendingIds = new Set(pending.map(layer => layer.id));
      setFetchingIds(prev => combineSets(prev, pendingIds));
    }

    Promise.all(pending.map(loadLayer));

    loadedLinksRef.current = selectedLayers.reduce<Record<string, string>>(
      (acc, layer) =>
        loadedLinksRef.current[layer.id]
          ? { ...acc, [layer.id]: loadedLinksRef.current[layer.id] }
          : acc,
      {},
    );
  }, [selectedLayers, featureData, storeLayerData, clearFetching]);

  const layers = useMemo(
    () =>
      selectedLayers.flatMap(layer => {
        const layerData = featureData[layer.id];
        return layerData ? [{ ...layer, ...layerData, visible: true }] : [];
      }),
    [selectedLayers, featureData],
  );

  const add = useCallback(
    async (input: ArcgisLayerCreateInput): Promise<ArcgisLayer | null> => {
      const result = await createLayer({ variables: { input } });
      return result.data?.arcgisLayerCreate ?? null;
    },
    [createLayer],
  );

  const update = useCallback(
    async (input: ArcgisLayerUpdateInput): Promise<ArcgisLayer | null> => {
      const result = await updateLayer({ variables: { input } });
      const updated = result.data?.arcgisLayerUpdate ?? null;

      // When the link changes, the stored feature data is stale. Re-sync it in
      // place (the selection effect skips layers that already have data).
      if (
        updated &&
        featureData[updated.id] &&
        loadedLinksRef.current[updated.id] !== updated.link
      ) {
        setFetchingIds(prev => addToSet(prev, updated.id));

        try {
          await storeLayerData(updated);
          loadedLinksRef.current[updated.id] = updated.link;
        } catch {
          Snackbar.error({
            message: `Unable to sync "${updated.name}". Please try again.`,
          });
        } finally {
          clearFetching(updated.id);
        }
      }

      return updated;
    },
    [updateLayer, featureData, storeLayerData, clearFetching],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await deleteLayer({ variables: { id } });
    },
    [deleteLayer],
  );

  const refresh = useCallback(
    async (layer: ArcgisLayer): Promise<void> => {
      setFetchingIds(prev => addToSet(prev, layer.id));

      try {
        await storeLayerData(layer);
        loadedLinksRef.current[layer.id] = layer.link;
        Snackbar.info({ message: `"${layer.name}" synced` });
      } catch {
        Snackbar.error({
          message: `Unable to sync "${layer.name}". Please try again.`,
        });
      } finally {
        clearFetching(layer.id);
      }
    },
    [storeLayerData, clearFetching],
  );

  const value = useMemo(
    () => ({
      arcgisDictionary,
      layers,
      loading,
      fetchingIds,
      add,
      update,
      remove,
      refresh,
    }),
    [
      arcgisDictionary,
      layers,
      loading,
      fetchingIds,
      add,
      update,
      remove,
      refresh,
    ],
  );

  return (
    <ArcgisLayersContext.Provider value={value}>
      {children}
    </ArcgisLayersContext.Provider>
  );
};

export default ArcgisLayersProvider;
